import {
  FlightsAdvancedFilterType,
  FlightsCabinClass,
  FlightsJourneyCriteriaInput,
  FlightsSearchPreferencesInput,
  FlightsTravelerDetailsInput,
  FlightsTravelerType
} from '__generated__/api';
import { INFANT_AGE_LIMIT } from 'utility';

const maxLegs = 5;

const getTravelerDetails = (
  numberOfAdults: string,
  numberOfSeniors: string,
  ages: string = null,
  infantInLap: string = null
): FlightsTravelerDetailsInput[] => {
  const travelerDetails: Array<FlightsTravelerDetailsInput> = [];
  let totalNumberOfAdults = 0;
  if (numberOfAdults) {
    totalNumberOfAdults += parseInt(numberOfAdults);
  }
  if (numberOfSeniors) {
    totalNumberOfAdults += parseInt(numberOfSeniors);
  }
  if (totalNumberOfAdults != 0) {
    travelerDetails.push({
      ages: null,
      count: totalNumberOfAdults,
      travelerType: FlightsTravelerType.ADULT
    });
    if (ages) {
      const agesArray = ages.split('|').map(i => parseInt(i, 10));
      const childrenAges = agesArray.filter(function (childAges) {
        return childAges > INFANT_AGE_LIMIT;
      });
      const infantAges = agesArray.filter(function (childAges) {
        return childAges <= INFANT_AGE_LIMIT;
      });
      if (childrenAges.length > 0) {
        travelerDetails.push({
          ages: childrenAges,
          count: childrenAges.length,
          travelerType: FlightsTravelerType.CHILD
        });
      }
      if (infantAges.length > 0) {
        if (infantInLap && String(infantInLap) == 'Y') {
          travelerDetails.push({
            ages: infantAges,
            count: infantAges.length,
            travelerType: FlightsTravelerType.INFANT_IN_LAP
          });
        } else {
          travelerDetails.push({
            ages: infantAges,
            count: infantAges.length,
            travelerType: FlightsTravelerType.INFANT_IN_SEAT
          });
        }
      }
    }

    return travelerDetails;
  }

  return null;
};

const splitAndMap = (value: string): Map<string, string> => {
  if (!value || value.length === 0) {
    return;
  }
  const finalMap = new Map();
  let index = 0;
  const splitValues = value.split('|');
  while (index < splitValues.length) {
    const keyAndValue = splitValues[index].split(':');
    if (keyAndValue.length == 2) {
      finalMap.set(keyAndValue[0], keyAndValue[1]);
    }
    index++;
  }

  return finalMap;
};

const getFlightJourneyCriteria = (
  departureCities: string,
  arrivalCities: string,
  departureDates: string
): FlightsJourneyCriteriaInput[] => {
  if (departureCities && arrivalCities && departureDates) {
    const departureDatesMap: Map<string, string> = splitAndMap(departureDates);
    const departureCitiesMap: Map<string, string> = splitAndMap(departureCities);
    const arrivalCitiesMap: Map<string, string> = splitAndMap(arrivalCities);
    const flightJourneyInput: Array<FlightsJourneyCriteriaInput> = [];
    for (let leg = 1; leg < maxLegs; ++leg) {
      const keyValue: string = 'L' + leg;
      if (!departureCitiesMap.has(keyValue) || !arrivalCitiesMap.has(keyValue) || !departureDatesMap.has(keyValue)) {
        break;
      }

      const date = new Date(departureDatesMap.get(keyValue));
      flightJourneyInput.push({
        departureDate: {
          day: date.getUTCDate(),
          month: date.getUTCMonth() + 1,
          year: date.getUTCFullYear()
        },
        destination: arrivalCitiesMap.get(keyValue),
        origin: departureCitiesMap.get(keyValue)
      });
    }

    return flightJourneyInput;
  }

  return null;
};

const getCabinClassFromMetaCode = (cabinMetaCode: string): FlightsCabinClass => {
  switch (cabinMetaCode) {
    case '2':
      return FlightsCabinClass.BUSINESS;
    case '1':
      return FlightsCabinClass.FIRST;
    case '5':
      return FlightsCabinClass.PREMIUM_ECONOMY;
    default:
      return FlightsCabinClass.COACH;
  }
};

const getSearchPreferences = (
  carrier: string,
  cabinClass: string,
  refundability: string
): FlightsSearchPreferencesInput => {
  return {
    airline: carrier,
    cabinClass: getCabinClassFromMetaCode(cabinClass),
    advancedFilters: refundability && String(refundability) == 'Y' ? Array(FlightsAdvancedFilterType.REFUNDABLE) : null
  };
};

export const MetaQueryAdapter = {
  getTravelerDetails,
  splitAndMap,
  getFlightJourneyCriteria,
  getCabinClassFromMetaCode,
  getSearchPreferences
};
